import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useParams, Link, useSearchParams, useLocation } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useOrgStore } from '@/stores/orgStore'
import { usePublishPageData } from '@/stores/pageDataStore'
import { inventoriesApi } from '@/api/clients/inventoriesApi'
import { sourcesApi } from '@/api/clients/sourcesApi'
import { scoringConfigApi } from '@/api/clients/scoringConfigApi'
import { assetsApi } from '@/api/clients/assetsApi'
import { economicsApi } from '@/api/clients/economicsApi'
import { analyticsApi } from '@/api/clients/analyticsApi'
import { alertsApi } from '@/api/clients/alertsApi'
import { policiesApi } from '@/api/clients/policiesApi'
import type { Economics, InventoryScoringOverride, Band, AssetListItem, MoneyPoint, TrendPoint } from '@/api/types'
import { MoneyTrendLine } from '@/components/charts/MoneyTrendLine'
import { TrendLine } from '@/components/charts/TrendLine'
import { PageHeader, Card, Button, Field, TextInput, SelectInput, Stat } from '@/components/common/ui'
import { Badge, BandBadge } from '@/components/common/Badge'
import { Modal } from '@/components/common/Modal'
import { Spinner, ErrorState, EmptyState, QueryState } from '@/components/common/State'
import { InventoryForm, type InventoryFormValue } from '@/components/inventory/InventoryForm'
import { MoneyValue } from '@/components/common/MoneyValue'
import { formatDate } from '@/components/common/format'
import { isNotFound } from '@/api/http'

type Tab = 'risk' | 'assets' | 'overview' | 'sources' | 'formula'

// Economics ($ ground-truth) fields, grouped by which exposure cluster they drive
// (see internal/economics/calc.go). `money` fields render as currency. The ROI group
// does NOT affect $-at-risk exposure — only remediation ROI.
// Per-component $-at-risk weight labels, in the backend's weight-vector order.
const EXPOSURE_WEIGHT_LABELS = [
  'Revenue',
  'Downtime',
  'Transaction',
  'SLA',
  'Breach',
  'HNDL',
  'RPO',
  'Regulatory',
]

type EconField = { key: keyof Economics; label: string; money?: boolean }
const ECON_GROUPS: { title: string; hint: string; fields: EconField[] }[] = [
  {
    title: 'Outage exposure',
    hint: 'revenue / downtime / transaction / SLA loss during an outage',
    fields: [
      { key: 'annual_revenue', label: 'Annual revenue', money: true },
      { key: 'downtime_cost_per_hour', label: 'Downtime cost / hour', money: true },
      { key: 'expected_outage_hours', label: 'Expected outage hours (RTO)' },
      { key: 'peak_tps', label: 'Peak TPS' },
      { key: 'avg_txn_value', label: 'Avg transaction value', money: true },
      { key: 'sla_penalty_per_hour', label: 'SLA penalty / hour', money: true },
    ],
  },
  {
    title: 'Data exposure',
    hint: 'breach + harvest-now-decrypt-later + RPO data loss',
    fields: [
      { key: 'data_records', label: 'Data records' },
      { key: 'cost_per_record', label: 'Cost per record', money: true },
      { key: 'data_write_rate_per_hour', label: 'Data write rate / hour' },
      { key: 'rpo_hours', label: 'RPO (hours)' },
      { key: 'data_volume_in_flight_per_hour', label: 'Data volume in-flight / hour' },
    ],
  },
  {
    title: 'Regulatory exposure',
    hint: 'penalty ceiling (probabilities apply to ROI, not exposure)',
    fields: [
      { key: 'regulatory_max_penalty', label: 'Regulatory max penalty', money: true },
      { key: 'regulatory_penalty_per_day', label: 'Regulatory penalty / day', money: true },
      { key: 'expected_outage_days', label: 'Expected outage days' },
    ],
  },
  {
    title: 'Remediation ROI (not exposure)',
    hint: 'feeds fix-all ROI, not the $-at-risk headline',
    fields: [
      { key: 'incident_probability', label: 'Incident probability (annual)' },
      { key: 'enforcement_probability', label: 'Enforcement probability (annual)' },
      { key: 'migration_cost', label: 'Migration cost', money: true },
      { key: 'migration_cost_per_asset', label: 'Migration cost / asset', money: true },
      { key: 'remediation_effort_days', label: 'Remediation effort (days)' },
      { key: 'fte_daily_rate', label: 'FTE daily rate', money: true },
      { key: 'crypto_agility_multiplier', label: 'Crypto agility multiplier' },
    ],
  },
]

export default function InventoryDetails() {
  const { id = '' } = useParams()
  const { currentOrgId: orgId } = useOrgStore()
  // Persist the active tab in the URL so browser/page Back returns to the same tab.
  const [searchParams, setSearchParams] = useSearchParams()
  const TAB_KEYS: Tab[] = ['risk', 'assets', 'overview', 'sources', 'formula']
  const tabParam = searchParams.get('tab') as Tab | null
  const tab: Tab = tabParam && TAB_KEYS.includes(tabParam) ? tabParam : 'risk'
  const setTab = (t: Tab) =>
    setSearchParams(
      (p) => {
        p.set('tab', t)
        return p
      },
      { replace: true },
    )

  const inv = useQuery({
    queryKey: ['inventory', orgId, id],
    queryFn: () => inventoriesApi.get(orgId, id),
    enabled: !!orgId && !!id,
  })

  usePublishPageData(
    useMemo(
      () =>
        inv.data
          ? {
              page: 'Inventory detail',
              tab,
              name: inv.data.name,
              business_unit: inv.data.business_unit,
              criticality: inv.data.criticality,
              data_classification: inv.data.data_classification,
              regulatory_scope: inv.data.regulatory_scope,
              regulatory_frameworks: inv.data.regulatory_frameworks,
              owners: inv.data.owners,
            }
          : null,
      [inv.data, tab]
    )
  )

  if (inv.isLoading) return <Spinner />
  if (isNotFound(inv.error))
    return (
      <EmptyState
        icon="search_off"
        title="Inventory not found"
        hint="This inventory may have been deleted, or the link points to another organization."
        action={
          <div className="flex gap-4 text-sm">
            <Link to="/" className="text-primary dark:text-secondary hover:underline">
              Back to dashboard
            </Link>
            <Link to="/inventories" className="text-primary dark:text-secondary hover:underline">
              All inventories
            </Link>
          </div>
        }
      />
    )
  if (inv.error) return <ErrorState error={inv.error} />
  const inventory = inv.data!

  const tabs: { key: Tab; label: string; icon: string }[] = [
    { key: 'risk', label: 'Risk', icon: 'shield' },
    { key: 'assets', label: 'Assets', icon: 'table_rows' },
    { key: 'overview', label: 'Business context', icon: 'description' },
    { key: 'sources', label: 'Sources', icon: 'dataset' },
    { key: 'formula', label: 'Formula overrides', icon: 'tune' },
  ]

  return (
    <div>
      <div className="mb-2">
        <Link
          to="/inventories"
          className="inline-flex items-center gap-1 text-sm text-subtle-light dark:text-subtle-dark hover:text-primary dark:hover:text-secondary"
        >
          <span className="material-icons text-base">arrow_back</span> Inventories
        </Link>
      </div>
      <PageHeader
        title={inventory.name}
        subtitle={inventory.description || inventory.business_unit}
      />

      {/* Tags — under the description (parity with qtz-discovery-portal) */}
      {inventory.tags && inventory.tags.length > 0 && (
        <div className="-mt-4 mb-6 flex flex-wrap gap-1.5">
          {inventory.tags.map((t) => (
            <span
              key={t}
              className="px-2.5 py-0.5 text-xs rounded-full bg-primary/10 dark:bg-secondary/15 text-primary dark:text-secondary border border-primary/20 dark:border-secondary/25 font-medium"
            >
              {t}
            </span>
          ))}
        </div>
      )}

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-border-light dark:border-border-dark mb-5">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.key
                ? 'border-primary text-primary dark:border-secondary dark:text-secondary'
                : 'border-transparent text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark'
            }`}
          >
            <span className="material-icons text-base">{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'risk' && (
        <RiskTab
          orgId={orgId}
          invId={id}
          onSetEconomics={() => setTab('formula')}
          onViewSources={() => setTab('sources')}
        />
      )}
      {tab === 'assets' && <InventoryAssetsTab orgId={orgId} invId={id} />}
      {tab === 'overview' && <OverviewTab inventory={inventory} orgId={orgId} />}
      {tab === 'sources' && <SourcesTab orgId={orgId} invId={id} />}
      {tab === 'formula' && <FormulaOverridesTab orgId={orgId} invId={id} />}
    </div>
  )
}

// ── Overview ──────────────────────────────────────────────────────────────────

function raciSummary(owners: unknown): string {
  const o = (owners ?? {}) as Record<string, unknown>
  const parts = (['responsible', 'accountable', 'consulted', 'informed'] as const)
    .map((k) => {
      const v = o[k]
      const list = Array.isArray(v) ? v : typeof v === 'string' && v ? [v] : []
      return list.length ? `${k[0].toUpperCase()}: ${list.join(', ')}` : ''
    })
    .filter(Boolean)
  return parts.length ? parts.join(' · ') : '—'
}

// ── Risk tab ──────────────────────────────────────────────────────────────────

const BAND_ORDER: Band[] = ['critical', 'high', 'medium', 'low', 'info']
const BAND_HEX: Record<string, string> = {
  critical: '#C22221',
  high: '#e8734f',
  medium: '#eab308',
  low: '#94C8E4',
  info: '#94a3b8',
}

// Migration progress (transition_state.status) — matches MIGRATION_META on the asset page.
const MIGRATION_ORDER = ['completed', 'in_progress', 'not_started'] as const
const MIGRATION_HEX: Record<string, string> = {
  completed: '#22a06b',
  in_progress: '#eab308',
  not_started: '#94a3b8',
}
const MIGRATION_LABEL: Record<string, string> = {
  completed: 'Migrated',
  in_progress: 'Migrating',
  not_started: 'Not migrated',
}

// RiskTab shows the inventory's aggregate risk (its own detail view), computed from the
// inventory's assets + economics — mirroring what the asset detail page does per asset.
const INV_TREND_RANGES = [30, 90, 180] as const

// Critical+High EWMA trend for a single inventory (mirrors the Dashboard Risk Trend
// card). Points accrue from per-inventory snapshots taken on each ingest/edit.
function InventoryTrendCard({
  points,
  loading,
}: {
  points: TrendPoint[] | undefined
  loading: boolean
}) {
  const [days, setDays] = useState<(typeof INV_TREND_RANGES)[number]>(90)
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000
  const filtered = useMemo(
    () => (points ?? []).filter((p) => new Date(p.taken_at).getTime() >= cutoff),
    [points, cutoff]
  )
  return (
    <Card title="Risk Trend">
      <div className="-mt-1 mb-3 flex items-center justify-end gap-1">
        {INV_TREND_RANGES.map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            className={`px-2 py-0.5 text-xs rounded-md font-medium transition-colors ${
              days === d
                ? 'bg-primary/10 text-primary dark:text-secondary'
                : 'text-subtle-light dark:text-subtle-dark hover:bg-primary/5'
            }`}
          >
            {d}d
          </button>
        ))}
      </div>
      <div className="h-56">
        {loading ? (
          <Spinner />
        ) : filtered.length > 0 ? (
          <TrendLine points={filtered} />
        ) : (
          <EmptyState
            icon="show_chart"
            title={points && points.length > 0 ? `No snapshots in the last ${days} days` : 'Trend builds over time'}
          />
        )}
      </div>
    </Card>
  )
}

// $-at-Risk trend for a single inventory (mirrors the Dashboard card, no model-change
// marker). Points accrue from per-inventory snapshots taken on each ingest/edit.
function InventoryMoneyTrendCard({
  points,
  currency,
  loading,
}: {
  points: MoneyPoint[] | undefined
  currency?: string
  loading: boolean
}) {
  const [days, setDays] = useState<(typeof INV_TREND_RANGES)[number]>(90)
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000
  const filtered = useMemo(
    () => (points ?? []).filter((p) => new Date(p.taken_at).getTime() >= cutoff),
    [points, cutoff]
  )
  return (
    <Card title="$-at-Risk Trend">
      <div className="-mt-1 mb-3 flex items-center justify-end gap-1">
        {INV_TREND_RANGES.map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            className={`px-2 py-0.5 text-xs rounded-md font-medium transition-colors ${
              days === d
                ? 'bg-primary/10 text-primary dark:text-secondary'
                : 'text-subtle-light dark:text-subtle-dark hover:bg-primary/5'
            }`}
          >
            {d}d
          </button>
        ))}
      </div>
      <div className="h-56">
        {loading ? (
          <Spinner />
        ) : filtered.length > 0 ? (
          <MoneyTrendLine points={filtered} currency={currency} />
        ) : (
          <EmptyState
            icon="show_chart"
            title={points && points.length > 0 ? `No snapshots in the last ${days} days` : 'Trend builds over time'}
          />
        )}
      </div>
    </Card>
  )
}

function RiskTab({
  orgId,
  invId,
  onSetEconomics,
  onViewSources,
}: {
  orgId: string
  invId: string
  onSetEconomics: () => void
  onViewSources: () => void
}) {
  const assets = useQuery({
    queryKey: ['inventory-assets', orgId, invId],
    queryFn: () => assetsApi.list(orgId, { inventory_id: invId, limit: 200 }),
    enabled: !!orgId && !!invId,
  })
  const impact = useQuery({
    queryKey: ['inventory-impact', orgId, invId],
    queryFn: () => economicsApi.inventoryImpact(orgId, invId, 'exposure'),
    enabled: !!orgId && !!invId,
    retry: false,
  })
  const roi = useQuery({
    queryKey: ['inventory-roi', orgId, invId],
    queryFn: () => economicsApi.remediationRoi(orgId, invId),
    enabled: !!orgId && !!invId,
    retry: false,
  })
  const trend = useQuery({
    queryKey: ['inventory-trends', orgId, invId],
    queryFn: () => analyticsApi.inventoryTrends(orgId, invId),
    enabled: !!orgId && !!invId,
    retry: false,
  })
  const moneyTrend = useQuery({
    queryKey: ['inventory-money-trends', orgId, invId],
    queryFn: () => analyticsApi.inventoryMoneyTrends(orgId, invId),
    enabled: !!orgId && !!invId,
    retry: false,
  })
  // Org-wide lists scoped to this inventory client-side.
  const alertsQ = useQuery({
    queryKey: ['alerts', orgId],
    queryFn: () => alertsApi.list(orgId),
    enabled: !!orgId,
    retry: false,
  })
  const waiversQ = useQuery({
    queryKey: ['waivers', orgId],
    queryFn: () => policiesApi.waivers(orgId),
    enabled: !!orgId,
    retry: false,
  })

  const { dist, total, worst, critHigh, hndlClosed, topSources, assetIds, migration, expiringCerts } =
    useMemo(() => {
    const rows = assets.data ?? []
    const dist: Record<string, number> = { critical: 0, high: 0, medium: 0, low: 0, info: 0 }
    const assetIds = new Set(rows.map((a) => a.id))
    // Migration progress buckets (transition_state.status; default/unknown → not_started).
    const migration = { completed: 0, in_progress: 0, not_started: 0 }
    // Certificates expiring within 90 days (include already-expired), soonest first.
    const now = Date.now()
    const horizon = now + 90 * 24 * 60 * 60 * 1000
    const expiringCerts: { id: string; name: string; expiry: string; days: number }[] = []
    // Aggregate assets by their owning source → per-source risk direction.
    const srcMap = new Map<
      string,
      { id: string; name: string; count: number; critHigh: number; peakScore: number; dist: Record<string, number> }
    >()
    for (const a of rows) {
      dist[a.band] = (dist[a.band] ?? 0) + 1
      // Migration state by posture + manual override (mirrors the backend rollup):
      // quantum-safe (or manually completed) = migrated; hybrid/partial (or manually
      // in-progress) = migrating; else not started.
      const mstatus = a.transition_state?.status
      const q = a.quantum_risk
      if (mstatus === 'completed' || q === 'safe') migration.completed += 1
      else if (mstatus === 'in_progress' || q === 'hybrid' || q === 'partial') migration.in_progress += 1
      else migration.not_started += 1
      if (a.cert_expiry) {
        const t = new Date(a.cert_expiry).getTime()
        if (!Number.isNaN(t) && t <= horizon) {
          expiringCerts.push({
            id: a.id,
            name: a.name,
            expiry: a.cert_expiry,
            days: Math.ceil((t - now) / (24 * 60 * 60 * 1000)),
          })
        }
      }
      const id = a.source_id
      const s =
        srcMap.get(id) ??
        {
          id,
          name: a.source_name || '—',
          count: 0,
          critHigh: 0,
          peakScore: 0,
          dist: { critical: 0, high: 0, medium: 0, low: 0, info: 0 },
        }
      s.count += 1
      s.dist[a.band] = (s.dist[a.band] ?? 0) + 1
      if (a.band === 'critical' || a.band === 'high') s.critHigh += 1
      if (a.score > s.peakScore) s.peakScore = a.score
      srcMap.set(id, s)
    }
    const topSources = [...srcMap.values()]
      .map((s) => ({ ...s, worstBand: BAND_ORDER.find((b) => s.dist[b] > 0) ?? 'info' }))
      .sort((a, b) => b.critHigh - a.critHigh || b.peakScore - a.peakScore || b.count - a.count)
      .slice(0, 8)
    expiringCerts.sort((a, b) => a.days - b.days)
    return {
      dist,
      total: rows.length,
      worst: BAND_ORDER.find((b) => dist[b] > 0) ?? 'info',
      critHigh: dist.critical + dist.high,
      hndlClosed: rows.filter((a) => a.hndl_window != null && a.hndl_window <= 0).length,
      topSources,
      assetIds,
      migration,
      expiringCerts,
    }
  }, [assets.data])

  // Org alerts/waivers scoped to this inventory (client-side cross-reference).
  const openAlerts = useMemo(
    () =>
      (alertsQ.data ?? []).filter(
        (al) => al.status !== 'resolved' && al.asset_id != null && assetIds.has(al.asset_id),
      ),
    [alertsQ.data, assetIds],
  )
  const activeWaivers = useMemo(
    () =>
      (waiversQ.data ?? []).filter(
        (w) =>
          w.status === 'active' &&
          ((w.scope_type === 'inventory' && w.scope_id === invId) ||
            (w.scope_id != null && assetIds.has(w.scope_id))),
      ),
    [waiversQ.data, assetIds, invId],
  )

  return (
    <QueryState
      isLoading={assets.isLoading}
      error={assets.error}
      isEmpty={total === 0 && !assets.isLoading}
      emptyProps={{
        icon: 'shield',
        title: 'No scored assets',
        hint: 'Ingest a CycloneDX/CBOM into a source — scoring runs automatically.',
      }}
    >
      <div className="space-y-5">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Worst band" value={<BandBadge band={worst} />} hint={`${critHigh} critical + high`} />
          <Stat
            label="$-at-risk"
            value={
              impact.data ? (
                <MoneyValue value={impact.data.total_at_risk} currency={impact.data.currency} />
              ) : (
                '—'
              )
            }
            hint={impact.data ? `exposure · ${impact.data.confidence} confidence` : undefined}
          />
          <Stat label="Assets scored" value={total} hint={`${hndlClosed} HNDL window closed`} />
          <Stat label="Critical + High" value={critHigh} hint="need migration" />
        </div>

        {/* Band distribution — single stacked bar + legend */}
        <Card title="Band distribution">
          <div className="flex h-9 w-full rounded-lg overflow-hidden bg-container-light dark:bg-container-dark">
            {BAND_ORDER.map((b) => {
              const pct = total > 0 ? (dist[b] / total) * 100 : 0
              return dist[b] > 0 ? (
                <div
                  key={b}
                  className="h-full flex items-center justify-center text-[11px] font-semibold text-white overflow-hidden"
                  style={{ width: `${pct}%`, backgroundColor: BAND_HEX[b] }}
                  title={`${b}: ${dist[b]} (${pct.toFixed(0)}%)`}
                >
                  {pct >= 8 ? dist[b] : ''}
                </div>
              ) : null
            })}
          </div>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-xs">
            {BAND_ORDER.map((b) => (
              <span key={b} className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: BAND_HEX[b] }} />
                <span className="text-text-light dark:text-text-dark capitalize">{b}</span>
                <span className="text-subtle-light dark:text-subtle-dark">
                  {dist[b]} · {total > 0 ? ((dist[b] / total) * 100).toFixed(0) : 0}%
                </span>
              </span>
            ))}
          </div>
        </Card>

        {/* Migration progress — transition_state rollup */}
        <Card title="Migration progress">
          <p className="mb-3 text-sm font-semibold text-text-light dark:text-text-dark">
            {total > 0 ? Math.round((migration.completed / total) * 100) : 0}% migrated
            <span className="ml-1 font-normal text-subtle-light dark:text-subtle-dark">
              ({migration.completed} of {total})
            </span>
          </p>
          <div className="flex h-9 w-full rounded-lg overflow-hidden bg-container-light dark:bg-container-dark">
            {MIGRATION_ORDER.map((m) => {
              const n = migration[m]
              const pct = total > 0 ? (n / total) * 100 : 0
              return n > 0 ? (
                <div
                  key={m}
                  className="h-full flex items-center justify-center text-[11px] font-semibold text-white overflow-hidden"
                  style={{ width: `${pct}%`, backgroundColor: MIGRATION_HEX[m] }}
                  title={`${MIGRATION_LABEL[m]}: ${n} (${pct.toFixed(0)}%)`}
                >
                  {pct >= 8 ? n : ''}
                </div>
              ) : null
            })}
          </div>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-xs">
            {MIGRATION_ORDER.map((m) => (
              <span key={m} className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: MIGRATION_HEX[m] }} />
                <span className="text-text-light dark:text-text-dark">{MIGRATION_LABEL[m]}</span>
                <span className="text-subtle-light dark:text-subtle-dark">
                  {migration[m]} · {total > 0 ? ((migration[m] / total) * 100).toFixed(0) : 0}%
                </span>
              </span>
            ))}
          </div>
        </Card>

        {/* Remediation ROI */}
        <Card title="Remediation ROI (migrate this inventory)">
          {roi.data ? (
            roi.data.cost_of_fix === 0 ? (
              <div className="text-xs text-text-light dark:text-text-dark">
                <MoneyValue
                  value={roi.data.delta_at_risk_avoided}
                  currency={roi.data.currency}
                  className="font-semibold text-success"
                />{' '}
                avoided. Migration cost &amp; labour aren&apos;t set for this inventory, so ROI can&apos;t be computed.
                <button
                  onClick={onSetEconomics}
                  className="ml-1 font-medium text-primary dark:text-secondary hover:underline"
                >
                  Set economics →
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2 text-sm text-text-light dark:text-text-dark">
                <div>
                  <p className="text-xs text-subtle-light dark:text-subtle-dark">Avoided</p>
                  <MoneyValue
                    value={roi.data.delta_at_risk_avoided}
                    currency={roi.data.currency}
                    className="font-semibold text-success"
                  />
                </div>
                <div>
                  <p className="text-xs text-subtle-light dark:text-subtle-dark">Cost of fix</p>
                  <MoneyValue value={roi.data.cost_of_fix} currency={roi.data.currency} />
                </div>
                <div>
                  <p className="text-xs text-subtle-light dark:text-subtle-dark">ROI</p>
                  <span className={`font-semibold ${roi.data.roi >= 0 ? 'text-success' : 'text-error'}`}>
                    {roi.data.roi.toFixed(2)}×
                  </span>
                </div>
              </div>
            )
          ) : (
            <p className="text-sm text-subtle-light dark:text-subtle-dark">ROI unavailable.</p>
          )}
        </Card>

        {/* Risk + $-at-Risk trends for this inventory (from per-inventory snapshots) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <InventoryTrendCard points={trend.data} loading={trend.isLoading} />
          <InventoryMoneyTrendCard
            points={moneyTrend.data}
            currency={impact.data?.currency}
            loading={moneyTrend.isLoading}
          />
        </div>

        {/* Governance / operational risk — alerts, waivers, expiring certs */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Open alerts */}
          <Card title={`Open alerts (${openAlerts.length})`}>
            {openAlerts.length === 0 ? (
              <p className="text-sm text-subtle-light dark:text-subtle-dark">None.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {openAlerts.slice(0, 3).map((al) => (
                  <li key={al.id} className="flex items-center gap-2">
                    <BandBadge band={al.band as Band} />
                    <span className="min-w-0 flex-1 truncate text-text-light dark:text-text-dark">
                      {al.asset_name}
                    </span>
                    <span className="shrink-0 text-xs text-subtle-light dark:text-subtle-dark">
                      {al.kind.replace(/_/g, ' ')}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <Link to="/alerts" className="mt-3 inline-block text-xs font-medium text-primary dark:text-secondary hover:underline">
              View all →
            </Link>
          </Card>

          {/* Accepted risk (active waivers) */}
          <Card title={`Accepted risk (${activeWaivers.length})`}>
            {activeWaivers.length === 0 ? (
              <p className="text-sm text-subtle-light dark:text-subtle-dark">No active waivers.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {activeWaivers.slice(0, 3).map((w) => (
                  <li key={w.id} className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate text-text-light dark:text-text-dark">
                      {w.scope_type === 'inventory' ? 'Inventory' : 'Asset'} waiver
                    </span>
                    <span className="shrink-0 text-xs text-subtle-light dark:text-subtle-dark">
                      exp {formatDate(w.expiry)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <Link to="/waivers" className="mt-3 inline-block text-xs font-medium text-primary dark:text-secondary hover:underline">
              View all →
            </Link>
          </Card>

          {/* Expiring certificates */}
          <Card title={`Expiring certificates (${expiringCerts.length})`}>
            {expiringCerts.length === 0 ? (
              <p className="text-sm text-subtle-light dark:text-subtle-dark">None within 90 days.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {expiringCerts.slice(0, 3).map((c) => (
                  <li key={c.id} className="flex items-center gap-2">
                    <Link
                      to={`/assets/${c.id}`}
                      className="min-w-0 flex-1 truncate text-primary dark:text-secondary hover:underline"
                    >
                      {c.name}
                    </Link>
                    <span
                      className={`shrink-0 text-xs ${c.days < 0 ? 'text-error' : 'text-subtle-light dark:text-subtle-dark'}`}
                    >
                      {c.days < 0 ? `expired ${-c.days}d ago` : `${c.days}d left`}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        {/* Top risky sources — where to check/fix first */}
        <Card title="Top risky sources" className="!p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-subtle-light dark:text-subtle-dark border-b border-border-light dark:border-border-dark">
                <th className="px-5 py-3 font-medium">Band</th>
                <th className="px-5 py-3 font-medium">Source</th>
                <th className="px-5 py-3 font-medium">Peak score</th>
                <th className="px-5 py-3 font-medium">Critical + High</th>
                <th className="px-5 py-3 font-medium">Assets</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-light dark:divide-border-dark">
              {topSources.map((s) => (
                <tr key={s.id} className="hover:bg-primary/5">
                  <td className="px-5 py-3">
                    <BandBadge band={s.worstBand} />
                  </td>
                  <td className="px-5 py-3">
                    <button
                      onClick={onViewSources}
                      className="text-primary dark:text-secondary hover:underline"
                    >
                      {s.name}
                    </button>
                  </td>
                  <td className="px-5 py-3 font-semibold text-text-light dark:text-text-dark">
                    {s.peakScore.toFixed(1)}
                  </td>
                  <td className="px-5 py-3 text-text-light dark:text-text-dark">{s.critHigh}</td>
                  <td className="px-5 py-3 text-subtle-light dark:text-subtle-dark">{s.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </QueryState>
  )
}

// ── Assets tab: all assets in this inventory ──────────────────────────────────

const BANDS: Band[] = ['critical', 'high', 'medium', 'low', 'info']
const ASSETS_PAGE_SIZE = 50

function InventoryAssetsTab({ orgId, invId }: { orgId: string; invId: string }) {
  const location = useLocation()
  const [band, setBand] = useState<Band | ''>('')
  const [sid, setSid] = useState('')
  const [search, setSearch] = useState('')
  const [offset, setOffset] = useState(0)
  const [pages, setPages] = useState<AssetListItem[][]>([])
  const [hasMore, setHasMore] = useState(false)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const isFetchingRef = useRef(false)

  const sourcesQ = useQuery({
    queryKey: ['sources', orgId, invId],
    queryFn: () => sourcesApi.list(orgId, invId),
    enabled: !!orgId && !!invId,
  })

  const q = useQuery({
    queryKey: ['inventory-all-assets', orgId, invId, band, sid, offset],
    queryFn: () =>
      assetsApi.list(orgId, {
        inventory_id: invId,
        band: band || undefined,
        source_id: sid || undefined,
        limit: ASSETS_PAGE_SIZE,
        offset,
      }),
    enabled: !!orgId && !!invId,
  })

  // Reset paging when a server-side filter (or inventory) changes. Clear pages too
  // so stale windows can't linger and inflate the tally before the offset-0 refetch.
  useEffect(() => {
    setOffset(0)
    setHasMore(false)
    setPages([])
  }, [band, sid, orgId, invId])

  // Accumulate fetched pages; offset===0 replaces (new filter/first page).
  useEffect(() => {
    if (!q.data) return
    const batch = q.data
    setPages((prev) => {
      if (offset === 0) return [batch]
      const next = [...prev]
      next[offset / ASSETS_PAGE_SIZE] = batch
      return next
    })
    setHasMore(batch.length === ASSETS_PAGE_SIZE)
  }, [q.data, offset])

  const rows = useMemo(() => {
    const seen = new Set<string>()
    return pages.flat().filter((a) => {
      if (seen.has(a.id)) return false
      seen.add(a.id)
      return true
    })
  }, [pages])

  // Client-side text search over loaded rows (name / algorithm / source).
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return rows
    return rows.filter((a) =>
      [a.name, a.algorithm, a.source_name ?? '']
        .some((v) => v.toLowerCase().includes(term)),
    )
  }, [rows, search])

  // Infinite scroll: load the next page when the sentinel nears the viewport.
  isFetchingRef.current = q.isFetching
  const loadMore = useCallback(() => setOffset((o) => o + ASSETS_PAGE_SIZE), [])
  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetchingRef.current) loadMore()
      },
      { rootMargin: '200px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasMore, loadMore])

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="w-40">
          <SelectInput value={band} onChange={(e) => setBand(e.target.value as Band | '')}>
            <option value="">All bands</option>
            {BANDS.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </SelectInput>
        </div>
        <div className="w-52">
          <SelectInput value={sid} onChange={(e) => setSid(e.target.value)}>
            <option value="">All sources</option>
            {(sourcesQ.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </SelectInput>
        </div>
        <div className="w-56">
          <TextInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name / algorithm…"
          />
        </div>
        {(band || sid || search) && (
          <button
            onClick={() => {
              setBand('')
              setSid('')
              setSearch('')
            }}
            className="text-xs font-medium text-primary dark:text-secondary hover:underline"
          >
            Clear
          </button>
        )}
        <span className="text-xs text-subtle-light dark:text-subtle-dark">
          {filtered.length} shown{hasMore && !search ? '+' : ''}
        </span>
      </div>

      <Card className="!p-0">
        <QueryState
          isLoading={q.isLoading && pages.length === 0}
          error={q.error}
          isEmpty={filtered.length === 0 && !q.isLoading}
          emptyProps={{
            icon: 'shield',
            title: 'No assets',
            hint: search
              ? 'No loaded assets match your search.'
              : 'Ingest a CycloneDX/CBOM into a source — scoring runs automatically.',
          }}
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-subtle-light dark:text-subtle-dark border-b border-border-light dark:border-border-dark">
                <th className="px-5 py-3 font-medium">Band</th>
                <th className="px-5 py-3 font-medium">Score</th>
                <th className="px-5 py-3 font-medium">Asset</th>
                <th className="px-5 py-3 font-medium">Algorithm</th>
                <th className="px-5 py-3 font-medium">HNDL</th>
                <th className="px-5 py-3 font-medium">Target</th>
                <th className="px-5 py-3 font-medium">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-light dark:divide-border-dark">
              {filtered.map((a) => (
                <tr key={a.id} className="hover:bg-primary/5">
                  <td className="px-5 py-3">
                    <BandBadge band={a.band} />
                  </td>
                  <td className="px-5 py-3 font-semibold text-text-light dark:text-text-dark">
                    {a.score?.toFixed(1)}
                  </td>
                  <td className="px-5 py-3 max-w-[240px]">
                    <Link
                      to={`/assets/${a.id}`}
                      state={{ backgroundLocation: location }}
                      className="text-primary dark:text-secondary hover:underline truncate block"
                    >
                      {a.name}
                    </Link>
                  </td>
                  <td className="px-5 py-3 text-text-light dark:text-text-dark">
                    {a.algorithm}
                    {a.key_size ? ` ${a.key_size}` : ''}
                  </td>
                  <td className="px-5 py-3 text-subtle-light dark:text-subtle-dark">
                    {a.hndl_window != null ? `${a.hndl_window.toFixed(1)}y` : '—'}
                  </td>
                  <td className="px-5 py-3 text-subtle-light dark:text-subtle-dark font-mono text-xs">
                    {a.recommended_target || '—'}
                  </td>
                  <td className="px-5 py-3 text-text-light dark:text-text-dark max-w-[180px] truncate">
                    {a.source_name || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </QueryState>
      </Card>

      {/* Infinite scroll: sentinel auto-loads the next page near the viewport. */}
      <div ref={sentinelRef} className="h-1" />
      <div className="flex items-center justify-center mt-3 text-xs text-subtle-light dark:text-subtle-dark">
        {q.isFetching && offset > 0
          ? 'Loading more…'
          : search
            ? `${filtered.length} of ${rows.length} loaded match`
            : rows.length > 0
              ? `${rows.length} assets loaded${hasMore ? ' — scroll for more' : ''}`
              : ''}
      </div>
    </div>
  )
}

function OverviewTab({
  inventory,
  orgId,
}: {
  inventory: import('@/api/types').Inventory
  orgId: string
}) {
  const qc = useQueryClient()
  const [showEdit, setShowEdit] = useState(false)

  const econ = useQuery({
    queryKey: ['economics', orgId, inventory.id],
    queryFn: () => inventoriesApi.getEconomics(orgId, inventory.id),
    enabled: !!orgId,
  })

  const importMut = useMutation({
    mutationFn: (from: 'discovery' | 'cmdb') => inventoriesApi.import(orgId, inventory.id, from),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sources', orgId, inventory.id] })
      qc.invalidateQueries({ queryKey: ['inventory', orgId, inventory.id] })
      qc.invalidateQueries({ queryKey: ['economics', orgId, inventory.id] })
      // Import seeds economics + assets → refresh $-derived and risk views.
      qc.invalidateQueries({ queryKey: ['summary', orgId] })
      qc.invalidateQueries({ queryKey: ['assets', orgId] })
      qc.invalidateQueries({ queryKey: ['inventory-impact', orgId, inventory.id] })
      qc.invalidateQueries({ queryKey: ['money-trend', orgId] })
      qc.invalidateQueries({ queryKey: ['money-trends', orgId] })
      qc.invalidateQueries({ queryKey: ['inventory-trends', orgId, inventory.id] })
      qc.invalidateQueries({ queryKey: ['inventory-money-trends', orgId, inventory.id] })
      qc.invalidateQueries({ queryKey: ['econ-last-changed', orgId] })
    },
  })

  const contextRows: [string, string][] = [
    ['Business unit', inventory.business_unit || '—'],
    ['Criticality', inventory.criticality || '—'],
    ['Data classification', inventory.data_classification || '—'],
    ['Environment', inventory.environment || '—'],
    ['Regulatory scope', inventory.regulatory_scope || '—'],
    ['Frameworks', inventory.regulatory_frameworks?.join(', ') || '—'],
    ['RACI owners', raciSummary(inventory.owners)],
    ['Origin', inventory.origin],
    ['CMDB ref', inventory.cmdb_ref || '—'],
    ['Created', formatDate(inventory.created_at)],
  ]

  const econData = econ.data ?? {}
  const currency = econData.currency ?? undefined

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card title="Business context" className="lg:col-span-2">
        <div className="flex justify-end -mt-2 mb-2">
          <Button variant="ghost" icon="edit" onClick={() => setShowEdit(true)}>
            Edit
          </Button>
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          {contextRows.map(([k, v]) => (
            <div key={k}>
              <dt className="label-sm text-subtle-light dark:text-subtle-dark">{k}</dt>
              <dd className="text-text-light dark:text-text-dark capitalize">{v}</dd>
            </div>
          ))}
        </dl>

        {/* Economics ($ ground truth) — grouped by exposure cluster (§12.5). */}
        <div className="mt-5 pt-4 border-t border-border-light dark:border-border-dark">
          <p className="label-sm text-subtle-light dark:text-subtle-dark mb-3">
            Economics ($ ground truth)
          </p>
          <div className="space-y-4">
            {ECON_GROUPS.map((g) => (
              <div key={g.title}>
                <p className="text-xs font-semibold text-text-light dark:text-text-dark">
                  {g.title}{' '}
                  <span className="font-normal text-subtle-light dark:text-subtle-dark">
                    — {g.hint}
                  </span>
                </p>
                <dl className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  {g.fields.map((f) => {
                    const val = econData[f.key] as number | null | undefined
                    return (
                      <div key={f.key}>
                        <dt className="label-sm text-subtle-light dark:text-subtle-dark">{f.label}</dt>
                        <dd className="text-text-light dark:text-text-dark">
                          {val == null ? (
                            '—'
                          ) : f.money ? (
                            <MoneyValue value={val} currency={currency} />
                          ) : (
                            val.toLocaleString()
                          )}
                        </dd>
                      </div>
                    )
                  })}
                </dl>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {showEdit && (
        <EditInventoryModal
          orgId={orgId}
          inventory={inventory}
          economics={econData}
          onClose={() => setShowEdit(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['inventory', orgId, inventory.id] })
            qc.invalidateQueries({ queryKey: ['economics', orgId, inventory.id] })
            // Economics values feed $-at-risk exposure — refresh the $-derived views.
            qc.invalidateQueries({ queryKey: ['summary', orgId] })
            qc.invalidateQueries({ queryKey: ['inventory-impact', orgId, inventory.id] })
            qc.invalidateQueries({ queryKey: ['money-trend', orgId] })
            qc.invalidateQueries({ queryKey: ['money-trends', orgId] })
            qc.invalidateQueries({ queryKey: ['inventory-trends', orgId, inventory.id] })
            qc.invalidateQueries({ queryKey: ['inventory-money-trends', orgId, inventory.id] })
            qc.invalidateQueries({ queryKey: ['econ-last-changed', orgId] })
            setShowEdit(false)
          }}
        />
      )}

      <Card title="Import business context">
        <p className="text-sm text-subtle-light dark:text-subtle-dark mb-4">
          One-click sync of business-context + the $ model (economics) + assets from M1 Discovery or
          your CMDB. CMDB import seeds economics from the criticality tier (provenance{' '}
          <span className="font-mono">cmdb</span>) without overwriting manual values.
        </p>
        <div className="space-y-2">
          <Button
            icon="cloud_sync"
            className="w-full"
            disabled={importMut.isPending}
            onClick={() => importMut.mutate('discovery')}
          >
            Import from M1 (Discovery)
          </Button>
          <Button
            icon="storage"
            className="w-full"
            disabled={importMut.isPending}
            onClick={() => importMut.mutate('cmdb')}
          >
            Import from CMDB
          </Button>
        </div>
        {importMut.data && (
          <p className="mt-3 text-sm text-success">
            Ingested {importMut.data.assets_ingested} assets from {importMut.data.source}.
            {importMut.data.economics_seeded && ' Economics seeded from CMDB tier.'}
          </p>
        )}
        {importMut.error && (
          <p className="mt-3 text-sm text-error">{(importMut.error as Error).message}</p>
        )}
      </Card>
    </div>
  )
}

// FormulaOverridesTab groups the per-inventory risk-score + $-at-risk exposure
// formula overrides in one place.
function FormulaOverridesTab({ orgId, invId }: { orgId: string; invId: string }) {
  return (
    <div className="space-y-4">
      <ScoringOverrideCard orgId={orgId} invId={invId} />
      <ExposureOverrideCard orgId={orgId} invId={invId} />
    </div>
  )
}

// ExposureOverrideCard edits the per-inventory $-at-risk composition override
// (strategy + weight pack + 8 weights). Blank inherits org; saving re-scores.
function ExposureOverrideCard({ orgId, invId }: { orgId: string; invId: string }) {
  const qc = useQueryClient()
  const econ = useQuery({
    queryKey: ['economics', orgId, invId],
    queryFn: () => inventoriesApi.getEconomics(orgId, invId),
    enabled: !!orgId && !!invId,
  })
  const [strategy, setStrategy] = useState('')
  const [pack, setPack] = useState('')
  const [weightStrs, setWeightStrs] = useState<string[] | null>(null)
  // Seed once the query settles — data may be null when no economics row exists yet.
  useEffect(() => {
    if (econ.isLoading || weightStrs !== null) return
    setStrategy(econ.data?.composition_strategy ?? '')
    setPack(econ.data?.weight_pack ?? '')
    setWeightStrs(
      Array.from({ length: 8 }, (_, i) => {
        const w = econ.data?.weights?.[i]
        return w == null ? '' : String(w)
      })
    )
  }, [econ.isLoading, econ.data, weightStrs])

  const save = useMutation({
    mutationFn: () => {
      const allBlank = (weightStrs ?? []).every((w) => w === '')
      return inventoriesApi.setEconomics(orgId, invId, {
        ...econ.data,
        composition_strategy: strategy === '' ? null : strategy,
        weight_pack: pack === '' ? null : pack,
        weights: allBlank ? null : (weightStrs ?? []).map((w) => (w === '' ? 0 : Number(w))),
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['economics', orgId, invId] })
      qc.invalidateQueries({ queryKey: ['summary', orgId] })
      qc.invalidateQueries({ queryKey: ['assets', orgId] })
      // $-derived views that must reflect the new exposure composition.
      qc.invalidateQueries({ queryKey: ['inventory-impact', orgId, invId] })
      qc.invalidateQueries({ queryKey: ['money-trend', orgId] })
      qc.invalidateQueries({ queryKey: ['money-trends', orgId] })
      qc.invalidateQueries({ queryKey: ['inventory-trends', orgId, invId] })
      qc.invalidateQueries({ queryKey: ['inventory-money-trends', orgId, invId] })
      qc.invalidateQueries({ queryKey: ['econ-last-changed', orgId] })
    },
  })

  if (econ.isLoading || weightStrs === null) return null
  const setWeight = (i: number, v: string) =>
    setWeightStrs((prev) => (prev ?? []).map((w, j) => (j === i ? v : w)))

  return (
    <Card title="Exposure formula override ($-at-risk)">
      <p className="text-xs text-subtle-light dark:text-subtle-dark mb-3">
        Overrides the org $-at-risk composition for this inventory. Blank inherits org. Saving
        re-scores + refreshes exposure.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Composition strategy">
          <SelectInput value={strategy ?? ''} onChange={(e) => setStrategy(e.target.value)}>
            <option value="">inherit org</option>
            {['weighted_sum', 'worst_of_cluster', 'worst_of_all'].map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </SelectInput>
        </Field>
        <Field label="Weight pack">
          <TextInput value={pack ?? ''} placeholder="inherit org" onChange={(e) => setPack(e.target.value)} />
        </Field>
      </div>
      <p className="mt-3 mb-1 text-xs text-subtle-light dark:text-subtle-dark">
        Component weights — blank all = inherit org; any value sets an explicit per-inventory vector
        (overrides the pack).
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {EXPOSURE_WEIGHT_LABELS.map((label, i) => (
          <Field key={label} label={label}>
            <TextInput
              type="number"
              value={weightStrs[i]}
              placeholder="inherit"
              onChange={(e) => setWeight(i, e.target.value)}
            />
          </Field>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Button variant="primary" icon="save" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save exposure override'}
        </Button>
        {save.error && <p className="text-sm text-error">{(save.error as Error).message}</p>}
      </div>
    </Card>
  )
}

// ScoringOverrideCard edits per-inventory risk-score overrides (§5.4). Blank field
// inherits the org config; saving re-scores just this inventory.
function ScoringOverrideCard({ orgId, invId }: { orgId: string; invId: string }) {
  const qc = useQueryClient()
  const ov = useQuery({
    queryKey: ['scoring-override', orgId, invId],
    queryFn: () => scoringConfigApi.getInventory(orgId, invId),
    enabled: !!orgId && !!invId,
  })
  const [form, setForm] = useState<InventoryScoringOverride | null>(null)
  useEffect(() => {
    if (ov.data && !form) setForm(ov.data)
  }, [ov.data, form])

  const save = useMutation({
    mutationFn: () => scoringConfigApi.setInventory(orgId, invId, form as InventoryScoringOverride),
    onSuccess: (d) => {
      setForm(d)
      qc.invalidateQueries({ queryKey: ['scoring-override', orgId, invId] })
      qc.invalidateQueries({ queryKey: ['assets', orgId] })
      qc.invalidateQueries({ queryKey: ['summary', orgId] })
    },
  })

  if (ov.isLoading || !form) return null

  const FIELDS: { key: keyof InventoryScoringOverride; label: string }[] = [
    { key: 'band_critical', label: 'Critical ≥' },
    { key: 'band_high', label: 'High ≥' },
    { key: 'band_medium', label: 'Medium ≥' },
    { key: 'band_low', label: 'Low ≥' },
    { key: 'bf_floor', label: 'BF floor' },
    { key: 'bf_data_weight', label: 'BF data' },
    { key: 'bf_criticality_weight', label: 'BF criticality' },
    { key: 'bf_regulatory_weight', label: 'BF regulatory' },
    { key: 'time_to_quantum_year', label: 'Q-year' },
    { key: 'default_shelf_life', label: 'Shelf life (yrs)' },
    { key: 'default_migration', label: 'Migration (yrs)' },
  ]
  const setField = (k: keyof InventoryScoringOverride, v: string) =>
    setForm((f) => ({ ...(f as InventoryScoringOverride), [k]: v === '' ? null : Number(v) }))

  return (
    <Card title="Risk-score overrides (this inventory)">
      <p className="text-xs text-subtle-light dark:text-subtle-dark mb-3">
        Blank inherits the org config (Settings → Risk scoring). Saving re-scores this inventory.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {FIELDS.map((f) => (
          <Field key={f.key} label={f.label}>
            <TextInput
              type="number"
              value={(form[f.key] as number | null) ?? ''}
              onChange={(e) => setField(f.key, e.target.value)}
              placeholder="inherit"
            />
          </Field>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Button variant="primary" icon="save" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save overrides'}
        </Button>
        {save.error && <p className="text-sm text-error">{(save.error as Error).message}</p>}
      </div>
    </Card>
  )
}

// EditInventoryModal edits business-context and economics together, then persists both
// (inventory PUT + economics PUT) so one form is the single edit surface for the Overview.
function EditInventoryModal({
  orgId,
  inventory,
  economics,
  onClose,
  onSaved,
}: {
  orgId: string
  inventory: import('@/api/types').Inventory
  economics: Economics
  onClose: () => void
  onSaved: () => void
}) {
  const [form, setForm] = useState<InventoryFormValue>({ name: inventory.name })
  const [econForm, setEconForm] = useState<Economics>({})

  const save = useMutation({
    mutationFn: async () => {
      await inventoriesApi.update(orgId, inventory.id, form)
      if (Object.keys(econForm).length > 0) {
        await inventoriesApi.setEconomics(orgId, inventory.id, { ...economics, ...econForm })
      }
    },
    onSuccess: onSaved,
  })

  return (
    <Modal
      title="Edit inventory"
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <InventoryForm initial={inventory} onChange={setForm} />

      <div className="mt-5 pt-4 border-t border-border-light dark:border-border-dark space-y-5">
        <p className="label-sm text-subtle-light dark:text-subtle-dark">Economics ($ ground truth)</p>
        {ECON_GROUPS.map((g) => (
          <div key={g.title}>
            <p className="text-xs font-semibold text-text-light dark:text-text-dark mb-2">
              {g.title}{' '}
              <span className="font-normal text-subtle-light dark:text-subtle-dark">— {g.hint}</span>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {g.fields.map((f) => (
                <Field key={f.key} label={f.label}>
                  <TextInput
                    type="number"
                    defaultValue={(economics[f.key] as number | undefined) ?? ''}
                    onChange={(e) =>
                      setEconForm((prev) => ({
                        ...prev,
                        [f.key]: e.target.value === '' ? null : Number(e.target.value),
                      }))
                    }
                  />
                </Field>
              ))}
            </div>
          </div>
        ))}
      </div>

      {save.error && <p className="mt-3 text-sm text-error">{(save.error as Error).message}</p>}
    </Modal>
  )
}

// ── Sources ─────────────────────────────────────────────────────────────────

function SourcesTab({ orgId, invId }: { orgId: string; invId: string }) {
  const qc = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const sources = useQuery({
    queryKey: ['sources', orgId, invId],
    queryFn: () => sourcesApi.list(orgId, invId),
    enabled: !!orgId && !!invId,
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['sources', orgId, invId] })

  return (
    <Card
      title="Sources"
      className="!p-0"
    >
      <div className="flex items-center justify-between px-5 py-3 border-b border-border-light dark:border-border-dark">
        <p className="text-xs text-subtle-light dark:text-subtle-dark">
          Each source is one origin of assets (CycloneDX file, M1 scan feed, CMDB CI).
        </p>
        <Button variant="primary" icon="add" onClick={() => setShowCreate(true)}>
          Create source
        </Button>
      </div>

      {sources.isLoading ? (
        <Spinner />
      ) : sources.error ? (
        <ErrorState error={sources.error} />
      ) : sources.data?.length === 0 ? (
        <EmptyState icon="dataset" title="No sources" hint="Create a source, then ingest a CycloneDX 1.7 file." />
      ) : (
        <ul className="divide-y divide-border-light dark:divide-border-dark">
          {sources.data?.map((src) => (
            <SourceRow
              key={src.id}
              orgId={orgId}
              invId={invId}
              source={src}
              onChange={invalidate}
            />
          ))}
        </ul>
      )}

      {showCreate && (
        <Modal title="Create source" onClose={() => setShowCreate(false)}>
          <CreateSourceForm
            orgId={orgId}
            invId={invId}
            onDone={() => {
              invalidate()
              setShowCreate(false)
            }}
          />
        </Modal>
      )}
    </Card>
  )
}

function SourceRow({
  orgId,
  invId,
  source,
  onChange,
}: {
  orgId: string
  invId: string
  source: import('@/api/types').Source
  onChange: () => void
}) {
  const fileInput = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  const [renaming, setRenaming] = useState(false)
  const [newName, setNewName] = useState(source.name)
  const [showEcon, setShowEcon] = useState(false)
  const [econForm, setEconForm] = useState<Partial<Economics>>({})

  const ingest = useMutation({
    mutationFn: (file: File) => sourcesApi.ingest(orgId, invId, source.id, file),
    onSuccess: onChange,
  })
  const refresh = useMutation({
    mutationFn: () => sourcesApi.refresh(orgId, invId, source.id),
    onSuccess: onChange,
  })
  const remove = useMutation({
    mutationFn: () => sourcesApi.remove(orgId, invId, source.id),
    onSuccess: onChange,
  })
  const rename = useMutation({
    mutationFn: () => sourcesApi.update(orgId, invId, source.id, { name: newName }),
    onSuccess: () => {
      setRenaming(false)
      onChange()
    },
  })
  const saveEcon = useMutation({
    mutationFn: () => inventoriesApi.setSourceEconomics(orgId, source.id, econForm),
    onSuccess: () => setShowEcon(false),
  })

  return (
    <li
      className={`px-5 py-3 ${dragOver ? 'bg-primary/5' : ''}`}
      onDragOver={(e) => {
        e.preventDefault()
        setDragOver(true)
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragOver(false)
        const file = e.dataTransfer.files?.[0]
        if (file) ingest.mutate(file)
      }}
    >
      <div className="flex items-center gap-3">
        <span className="material-icons text-subtle-light dark:text-subtle-dark">dataset</span>
        <div className="flex-1 min-w-0">
          {renaming ? (
            <div className="flex items-center gap-2">
              <TextInput value={newName} onChange={(e) => setNewName(e.target.value)} />
              <Button variant="ghost" icon="check" onClick={() => rename.mutate()} title="Save name" />
              <Button variant="ghost" icon="close" onClick={() => setRenaming(false)} title="Cancel" />
            </div>
          ) : (
            <>
              <p className="text-sm font-medium text-text-light dark:text-text-dark truncate">
                {source.name}
              </p>
              <p className="text-xs text-subtle-light dark:text-subtle-dark">
                {source.source_type} · last ingest {formatDate(source.last_ingested_at)}
              </p>
            </>
          )}
        </div>
        <Badge label={source.is_active ? 'active' : 'inactive'} variant={source.is_active ? 'success' : 'neutral'} />
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            icon="upload_file"
            onClick={() => fileInput.current?.click()}
            title="Upload CycloneDX"
          >
            {ingest.isPending ? '…' : 'Upload'}
          </Button>
          <Button variant="ghost" icon="refresh" onClick={() => refresh.mutate()} title="Refresh from upstream">
            {refresh.isPending ? '…' : ''}
          </Button>
          <Button variant="ghost" icon="edit" onClick={() => setRenaming(true)} title="Rename" />
          <Button variant="ghost" icon="payments" onClick={() => setShowEcon((v) => !v)} title="Source economics override" />
          <Button variant="ghost" icon="delete" onClick={() => remove.mutate()} title="Detach / delete" />
        </div>
      </div>

      {showEcon && (
        <div className="mt-2 p-3 rounded-xl bg-container-light dark:bg-container-dark">
          <p className="label-sm text-subtle-light dark:text-subtle-dark mb-2">
            Source economics override (§12.3)
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {(
              [
                ['annual_revenue', 'Annual revenue'],
                ['downtime_cost_per_hour', 'Downtime $/hr'],
                ['data_records', 'Data records'],
                ['migration_cost', 'Migration cost'],
              ] as [keyof Economics, string][]
            ).map(([k, label]) => (
              <Field key={k} label={label}>
                <TextInput
                  type="number"
                  onChange={(e) =>
                    setEconForm((f) => ({
                      ...f,
                      [k]: e.target.value === '' ? null : Number(e.target.value),
                    }))
                  }
                />
              </Field>
            ))}
          </div>
          <div className="mt-2 flex justify-end">
            <Button variant="primary" icon="save" disabled={saveEcon.isPending} onClick={() => saveEcon.mutate()}>
              {saveEcon.isPending ? 'Saving…' : 'Save override'}
            </Button>
          </div>
          {saveEcon.error && <p className="mt-1 text-xs text-error">{(saveEcon.error as Error).message}</p>}
        </div>
      )}
      <input
        ref={fileInput}
        type="file"
        accept=".json,.xml,application/json"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) ingest.mutate(file)
        }}
      />
      {(ingest.data || ingest.error) && (
        <p className={`mt-1 text-xs ${ingest.error ? 'text-error' : 'text-success'}`}>
          {ingest.error
            ? (ingest.error as Error).message
            : `Ingested ${ingest.data?.assets_ingested} assets — scoring runs async.`}
        </p>
      )}
      <p className="mt-1 text-[11px] text-subtle-light dark:text-subtle-dark">
        Drag & drop a CycloneDX 1.7 file here to ingest.
      </p>
    </li>
  )
}

function CreateSourceForm({
  orgId,
  invId,
  onDone,
}: {
  orgId: string
  invId: string
  onDone: () => void
}) {
  const [name, setName] = useState('')
  const [type, setType] = useState('cyclonedx_upload')
  const mut = useMutation({
    mutationFn: () => sourcesApi.create(orgId, invId, { name, source_type: type }),
    onSuccess: onDone,
  })
  return (
    <div className="space-y-4">
      <Field label="Name">
        <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="Java repo CBOM" />
      </Field>
      <Field label="Source type">
        <TextInput value={type} onChange={(e) => setType(e.target.value)} />
      </Field>
      {mut.error && <p className="text-sm text-error">{(mut.error as Error).message}</p>}
      <div className="flex justify-end">
        <Button variant="primary" disabled={!name || mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create'}
        </Button>
      </div>
    </div>
  )
}

